import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, DownloadSimple, ShieldCheck, ShieldWarning, ShieldSlash, Info } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const StateIcon = ({ ok, dim = false }) =>
  ok ? <ShieldCheck size={18} className="text-emerald-600"/>
     : dim ? <ShieldSlash size={18} className="text-slate-400"/>
           : <ShieldWarning size={18} className="text-amber-600"/>;

const ScoreCell = ({ label, value, max }) => (
  <div className="rounded-lg border p-3 bg-muted/30">
    <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 text-lg font-semibold">
      {value}<span className="text-muted-foreground text-sm">/{max}</span>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="rounded-xl border bg-card p-5 space-y-3">
    <h2 className="font-display font-semibold">{title}</h2>
    {children}
  </div>
);

const CheckRow = ({ ok, label, note, extra, critical }) => (
  <div className="flex items-start gap-3 py-1.5 border-b last:border-0">
    <StateIcon ok={ok}/>
    <div className="flex-1 text-sm">
      <div className="font-medium">
        {critical && <span className="text-[0.6rem] font-bold text-red-600 mr-1.5">⭐ CRITIQUE</span>}
        {label}
      </div>
      <div className="text-xs text-muted-foreground">{note}</div>
      {extra && <div className="text-[0.7rem] text-muted-foreground/80 mt-0.5 mono truncate">{extra}</div>}
    </div>
  </div>
);

export default function CyberAudit() {
  const nav = useNavigate();
  const [domain, setDomain] = useState("");
  const [testPassword, setTestPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);

  const run = async () => {
    if (!domain.trim()) { toast.error("Veuillez saisir un domaine"); return; }
    setBusy(true); setAudit(null);
    try {
      const r = await api.post("/modules/cyber-audit/run", {
        domain: domain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, ""),
        test_password: testPassword || null,
      });
      setAudit(r.data);
      toast.success(`Audit terminé · score ${r.data.score.total}/100`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'audit");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/modules/cyber-audit/${audit.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cyber-audit-${audit.domain}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Impossible de télécharger le PDF"); }
  };

  const scoreColor = (s) => s >= 75 ? "text-emerald-600" : s >= 50 ? "text-amber-600" : "text-red-600";

  return (
    <div className="p-6 max-w-5xl">
      <button onClick={() => nav("/modules")} data-testid="cyber-back" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={16}/> Modules
      </button>
      <h1 className="text-3xl font-display font-bold mb-1">Audit Cybersécurité</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Scan technique 100 % automatisé — DNS · SSL/TLS · en-têtes HTTP · DNSSEC · MTA-STS/TLS-RPT/BIMI · HIBP. Rapport PDF téléchargeable.
      </p>

      <div className="rounded-xl border bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900/60 p-4 mb-6 flex gap-3 items-start">
        <Info size={18} className="text-blue-700 dark:text-blue-300 shrink-0 mt-0.5"/>
        <div className="text-xs text-blue-900 dark:text-blue-100">
          Aucun questionnaire à remplir — l'audit interroge directement les serveurs DNS publics, le port 443 pour le certificat, et fait un GET HTTPS pour lire les en-têtes. Durée ~10 s.
        </div>
      </div>

      <div className="rounded-xl border bg-card p-5 space-y-4 mb-6">
        <div>
          <Label>Domaine à analyser</Label>
          <Input
            data-testid="cyber-domain-input"
            placeholder="exemple.fr"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground mt-1">Le domaine racine (sans https:// ni chemin). Le scan inclut les enregistrements DNS, le port 443 et un GET HTTPS.</p>
        </div>
        <div>
          <Label>Mot de passe à tester (optionnel)</Label>
          <Input
            data-testid="cyber-password-input"
            type="password"
            placeholder="Vérification HIBP (k-anonymity, aucun envoi en clair)"
            value={testPassword}
            onChange={(e) => setTestPassword(e.target.value)}
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground mt-1">Seul le SHA-1 tronqué (5 chars) est envoyé à HaveIBeenPwned — votre mot de passe ne quitte jamais votre navigateur.</p>
        </div>
        <div className="flex justify-end">
          <Button onClick={run} disabled={busy} data-testid="cyber-run-btn" size="lg">
            {busy ? "Analyse en cours…" : "Lancer l'audit"}
          </Button>
        </div>
      </div>

      {audit && (
        <div className="space-y-4" data-testid="cyber-audit-result">
          <div className="rounded-xl border bg-card p-5 flex items-center justify-between">
            <div>
              <h2 className="font-display font-semibold text-xl">Résultats · {audit.domain}</h2>
              <div className="text-xs text-muted-foreground">{new Date(audit.at).toLocaleString("fr-FR")}</div>
            </div>
            <div className="text-right">
              <div className={`text-4xl font-bold ${scoreColor(audit.score.total)}`}>{audit.score.total}<span className="text-lg text-muted-foreground">/100</span></div>
              <div className="text-xs text-muted-foreground">Score global</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <ScoreCell label="DNS email" value={audit.score.dns_email} max={30}/>
            <ScoreCell label="SSL/TLS" value={audit.score.ssl} max={20}/>
            <ScoreCell label="En-têtes HTTP" value={audit.score.http_headers} max={20}/>
            <ScoreCell label="DNS avancé" value={audit.score.advanced_dns} max={20}/>
            <ScoreCell label="HIBP" value={audit.score.hibp} max={10}/>
          </div>

          <Section title="Posture email (DNS)">
            {["mx", "spf", "dmarc", "dkim"].map((k) => {
              const c = audit.dns.checks[k] || {};
              return <CheckRow key={k} ok={c.ok} label={k.toUpperCase()} note={c.note}/>;
            })}
          </Section>

          <Section title="Certificat SSL / TLS">
            {audit.ssl.protocol || audit.ssl.ok ? (
              <>
                <CheckRow ok={audit.ssl.ok} label={`HTTPS · ${audit.ssl.protocol || "?"}`} note={audit.ssl.note}
                          extra={audit.ssl.issuer ? `Émis par ${audit.ssl.issuer} · cipher ${audit.ssl.cipher || "?"}` : ""}/>
                {audit.ssl.expires_at && (
                  <CheckRow
                    ok={audit.ssl.days_left > 30}
                    label="Expiration"
                    note={audit.ssl.days_left < 0
                      ? `⚠ Expiré depuis ${Math.abs(audit.ssl.days_left)} j`
                      : `${audit.ssl.days_left} j restants (le ${audit.ssl.expires_at})`}
                  />
                )}
              </>
            ) : (
              <div className="text-sm text-muted-foreground">{audit.ssl.note || audit.ssl.error || "Aucun HTTPS accessible"}</div>
            )}
          </Section>

          <Section title={`En-têtes de sécurité HTTP · ${audit.http_headers.note || ""}`}>
            {audit.http_headers.reachable ? (
              audit.http_headers.checks.map((c, i) => (
                <CheckRow key={i} ok={c.present} critical={c.critical} label={c.header} note={c.hint}
                          extra={c.value}/>
              ))
            ) : (
              <div className="text-sm text-muted-foreground">{audit.http_headers.note || audit.http_headers.error}</div>
            )}
          </Section>

          <Section title="DNS avancé & durcissement email">
            {[["dnssec", "DNSSEC"], ["mta_sts", "MTA-STS"], ["tls_rpt", "TLS-RPT"], ["bimi", "BIMI (logo Gmail)"]].map(([k, label]) => {
              const c = audit.advanced_dns[k] || {};
              return <CheckRow key={k} ok={c.ok} label={label} note={c.note}/>;
            })}
          </Section>

          {audit.hibp.checked && (
            <Section title="Mot de passe (HaveIBeenPwned)">
              <CheckRow
                ok={!audit.hibp.compromised}
                label="Fuite connue ?"
                note={audit.hibp.compromised
                  ? `⚠ Compromis dans ${audit.hibp.count.toLocaleString("fr-FR")} fuite(s) — à changer immédiatement`
                  : "Non trouvé dans les bases de fuites connues"}
              />
            </Section>
          )}

          <div className="flex justify-end">
            <Button onClick={downloadPdf} data-testid="cyber-pdf-btn">
              <DownloadSimple size={16} className="mr-1"/> Télécharger le rapport PDF
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
